import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { Link } from "react-router-dom";
import PreferenceCenter from "./PreferenceCenter";
import {
  getConsent,
  recordConsent,
  onOpenCookiePreferences,
  DEFAULT_PURPOSES,
} from "../lib/consent";

/**
 * CookieBanner — bannière CMP (bas d'écran) affichée seulement tant que
 * l'utilisateur n'a pas fait de choix. 3 actions : Tout accepter / Tout refuser /
 * Personnaliser. Reste accessible via événement global `itercatus:open-cookie-preferences`.
 */
export default function CookieBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Show only if no consent stored.
    setVisible(!getConsent());
    // Listen to external open events (footer link).
    const off = onOpenCookiePreferences(() => setPrefsOpen(true));
    return off;
  }, []);

  const acceptAll = async () => {
    setBusy(true);
    await recordConsent({ necessary: true, analytics: true, marketing: true, functional: true });
    setBusy(false);
    setVisible(false);
  };

  const rejectAll = async () => {
    setBusy(true);
    await recordConsent({ ...DEFAULT_PURPOSES, necessary: true });
    setBusy(false);
    setVisible(false);
  };

  const openPrefs = () => setPrefsOpen(true);
  const handlePrefsChange = (open) => {
    setPrefsOpen(open);
    if (!open && getConsent()) setVisible(false);
  };

  return (
    <>
      {visible && (
        <div
          className="fixed bottom-0 inset-x-0 z-[60] px-4 pb-4 pointer-events-none"
          role="dialog"
          aria-label="Bannière de consentement aux cookies"
          data-testid="cookie-banner"
        >
          <div className="mx-auto max-w-4xl pointer-events-auto bg-white/95 backdrop-blur border border-primary/20 shadow-2xl rounded-xl p-5 md:p-6">
            <div className="flex flex-col md:flex-row md:items-start md:gap-6">
              <div className="flex-1">
                <p className="font-heading text-lg text-primary mb-2">
                  {t("cookie.title")}
                </p>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {t("cookie.body")}
                  <Link
                    to="/confidentialite"
                    className="underline text-primary hover:text-accent"
                  >
                    {t("cookie.learnMore")}
                  </Link>
                  .
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 mt-4 md:mt-0 md:flex-shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={rejectAll}
                  disabled={busy}
                  data-testid="cookie-banner-reject"
                >
                  {t("cookie.reject")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openPrefs}
                  disabled={busy}
                  data-testid="cookie-banner-customize"
                >
                  {t("cookie.customize")}
                </Button>
                <Button
                  size="sm"
                  onClick={acceptAll}
                  disabled={busy}
                  data-testid="cookie-banner-accept"
                >
                  {t("cookie.accept")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      <PreferenceCenter open={prefsOpen} onOpenChange={handlePrefsChange} />
    </>
  );
}
